import React from "react";
import PropTypes from "prop-types";
import { withRouter } from "react-router";
import { registerUser } from "react-cognito";
import posthog from "posthog-js";
import ENVIRONMENT from "../../../../environments";
import {
  MailOutlined,
  LockOutlined,
  LoadingOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
} from "@ant-design/icons";
import { Input, Button, Row, Col, Form } from "antd";
import axios from "axios";
import Dashboard from "../../Dashboard";
import GoogleSignIn from "../LoginFormAnt/GoogleForm";
import LogInHeader from "../../../LogInHeader/LogInHeader";
import Footer from "../../../Footer/Footer";
import { submitToSalesForce, submitToHubSpot } from "../LoginFormAnt/utils";

class MinimalRegisterForm extends React.Component {
  formRef = React.createRef();
  posthogInitialized = false;
  registerPosthog = null;

  initPosthogIfNeeded = (utmSource) => {
    if (this.posthogInitialized) return;
    if (!utmSource) return;
    // Only track on production domain
    if (window.location.hostname !== 'app.all3d.ai') return;

    // Use a dedicated PostHog instance (separate API key/project) for Register page analytics
    this.registerPosthog = posthog.init(ENVIRONMENT.REGISTER_POSTHOG_API_KEY, {
      api_host: ENVIRONMENT.REGISTER_POSTHOG_API_HOST,
      autocapture: false,
      name:"register",
      capture_pageview: false,
      disable_session_recording: true,
      loaded: (ph) => {
        // Persist utm_source on the person for easier filtering
        ph.register({ utm_source: utmSource });
      },
    });
    this.posthogInitialized = true;
  };

  capturePosthog = (eventName, properties = {}) => {
    const utmSource = this.state.utmSource || properties.utm_source;
    if (!utmSource) return;
    if (window.location.hostname !== 'app.all3d.ai') return;

    this.initPosthogIfNeeded(utmSource);
    if (this.registerPosthog && typeof this.registerPosthog.capture === 'function') {
      this.registerPosthog.capture(eventName, { utm_source: utmSource, ...properties });
    }
  };

  constructor(props) {
    super(props);
    let URLParams = new URLSearchParams(window.location.search);
    let email = URLParams.get("email") || "";
    // Email addresses don't have spaces, so replace spaces with +
    email = email.replace(/ /g, '+');
    console.log("email", email);

    this.state = {
      error: "",
      email: email,
      password: "",
      isLoading: false,
      stage: 0,
      utmSource: "",
    };
  }

  static propTypes = {
    history: PropTypes.object.isRequired,
  };

  componentDidMount() {
    let URLParams = new URLSearchParams(window.location.search);
    let redirectURL = URLParams.get('redirectURL');
    let utmSource = URLParams.get("utm_source") || null;

    // Also check inside redirectURL if not found at top level
    if (!utmSource && redirectURL) {
      const redirectParams = new URLSearchParams(decodeURIComponent(redirectURL).split("?")[1]);
      utmSource = redirectParams.get("utm_source") || null;
    }

    // if the user is already signed in and redirectURL is for select-subscription,
    // skip signup and redirect to account-details with pricing modal open if plan is not free_trial_ai
    if(redirectURL && localStorage.getItem("all3d_jwt_token") && localStorage.getItem("username")){
      if(redirectURL.includes('select-subscription')){
        const redirectParams = new URLSearchParams(redirectURL.split("?")[1]);
        const plan = redirectParams.get("plan") || "";
        const planType = redirectParams.get("plan_type") || "";
        if (plan !== 'free_trial_ai')
          window.location.href = `/account-details?tab=subs&openPricingModal=true&plan=${plan}&plan_type=${planType}`;
        else
          window.location.href = '/';
        return;
      }
    }

    // Use the email from state (already has + preserved from constructor)
    if (this.state.email) {
      const email_to_lowercase = this.state.email.toLowerCase();
      this.setState({ email: email_to_lowercase });
      // Set the form field value with the corrected email
      if (this.formRef.current) {
        this.formRef.current.setFieldsValue({
          email: email_to_lowercase
        });
      }
    }

    this.setState({ utmSource: utmSource });

    if (utmSource && window.location.hostname === 'app.all3d.ai') {
      this.capturePosthog('minimal_register_page_opened', { utm_source: utmSource, email: this.state.email });
    }
  }

  loginUser = () => {
    this.setState({
      stage: 3,
    });
  };

  onSubmitWrapper = (values) => {
    this.capturePosthog('minimal_register_button_clicked');
    this.setState({ error: "" });
    
    const email = values.email.toLowerCase();
    
    // Check if email is unique
    this.setState({ isLoading: true });
    axios
      .post(ENVIRONMENT.FETCH_USERNAME, { email })
      .then((res) => {
        if (res.data.username == null) {
          // Email is unique, proceed with registration
          this.onSubmit(values);
        } else {
          this.setState({ isLoading: false });
          this.setState({
            error: "A user with this email address already exists",
          });
        }
      })
      .catch((error) => {
        this.setState({ isLoading: false });
        this.setState({
          error: "Error checking email. Please try again.",
        });
      });
  };

  onSubmit = (values) => {
    const email = values.email.toLowerCase();
    const password = values.password;
    
    // Use email as username
    const username = email;
    
    // Prepare payload for backend
    const payload = {
      email: email,
      username: username,
      user_group: "customer",
      profile_name: email.split('@')[0], // Extract name from email
    };

    const { store } = this.context;
    const state = store.getState();
    const userPool = state.cognito.userPool;
    const config = state.cognito.config;
    
    this.setState({ isLoading: true });
    
    // Register with Cognito
    registerUser(userPool, config, username, password, {
      email: email,
    }).then(
      (action) => {
        store.dispatch(action);
        this.setState({ stage: 2, isLoading: false });
        
        if (ENVIRONMENT.ENV === "production" && !email.includes("all3d")) {
          // submitToSalesForce(payload, this.state);
          submitToHubSpot(payload, this.state);
        }

        // Call simplified backend API
        axios.post(ENVIRONMENT.USER_SIGNUP_SIMPLE, payload)
          .then((res) => {
            console.log('User created in backend:', res.data);
          })
          .catch((err) => {
            console.error('Backend user creation failed:', err);
          });
      },
      (error) => {
        this.setState({ error, isLoading: false });
      }
    );
  };

  changeEmail = (event) => {
    this.setState({ email: event.target.value });
  };

  changePassword = (event) => {
    this.setState({ password: event.target.value });
  };

  handleGoogleClick = () => {
    this.capturePosthog('minimal_register_with_google_clicked');
  };

  render = () => {
    const formDiv = {
      display: "flex",
      "justify-content": "space-around",
      "margin-top": "25px",
    };

    const formStyle = {
      "background-color": "white",
      "border-radius": "5px",
    };

    return (
      <span>
        {this.state.stage === 0 ? (
          <>
            <LogInHeader/>
            <Row className='justify-on-mobile'>
              <Col span={12} className='hide-on-mobile'>
                <img
                  className="branding-image"
                  alt="Brand Image"
                  src={'/img/introImage.png'}
                />
              </Col>
              <Col xs={22} sm={22} md={24} lg={12} className='web-desktop-width'>
                <div className="login-frm" style={formDiv}>
                  <div style={formStyle}>
                    <div style={formDiv}>
                      <div style={formStyle}>
                        <Form
                          onFinish={this.onSubmitWrapper}
                          onFinishFailed={(e) => console.log(e)}
                          className="login-form"
                          ref={this.formRef}
                          initialValues={{ email: this.state.email || "" }}
                        >
                          <h3 className='manrope w-700 black-00 f-24'>
                            Sign up to try ALL3D for free
                          </h3>
                          
                          <Form.Item
                            name="email"
                            rules={[
                              {
                                type: "email",
                                message: <span className='manrope f-14 red'>Please enter a valid email</span>,
                              },
                              {
                                required: true,
                                message: <span className='manrope f-14 red'>Please enter your email</span>,
                              },
                              {
                                pattern: "^[^\\s]+(\\s+[^\\s]+)*$",
                                message: <span className='manrope f-14 red'>Spaces are not allowed</span>,
                              },
                            ]}
                          >
                            <Input
                              onChange={this.changeEmail}
                              className='manrope f-14 w-400 placeholder black'
                              placeholder="Email address"
                              prefix={<MailOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                            />
                          </Form.Item>

                          <Form.Item
                            name="password"
                            rules={[
                              {
                                required: true,
                                message: <span className='manrope f-14 red'>Please enter a password</span>,
                              },
                              {
                                pattern:
                                  "^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$",
                                message: <span className='manrope f-14 red'>Password must be 8+ characters with at least 1 uppercase, 1 lowercase, 1 digit, and 1 special character</span>,
                              },
                            ]}
                          >
                            <Input.Password
                              onChange={this.changePassword}
                              className='placeholder black custom-input-suffix manrope f-14 w-400'
                              type="password"
                              placeholder="Password"
                              prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                              iconRender={visible => (
                                <span
                                  style={{
                                    position: 'absolute',
                                    right: '10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                  }}
                                >
                                  {visible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                                </span>
                              )}
                            />
                          </Form.Item>

                          <Form.Item>
                            <Button
                              className="modal-okay square font-16 center-btn mt-16"
                              htmlType="submit"
                              disabled={this.state.isLoading}
                            >
                              Sign Up
                              {this.state.isLoading && <span>{" "}<LoadingOutlined className="ml-8" /></span>}
                            </Button>

                            <div className="manrope f-14 red text-center mt-10">{this.state.error}</div>
                          </Form.Item>
                          
                          <div className="text-center manrope f-14 w-500 black-00 mt-20">
                            Already have an ALL3D account? <a href="/">Log in</a>
                          </div>
                        </Form>

                        <>
                          <div style={{ display: "flex", alignItems: "center", margin: "20px 0" }}>
                            <hr style={{ flex: 1, border: "none", height: "1px", backgroundColor: "#ccc" }} />
                            <span style={{ margin: "0 10px 5px", color: "#666", fontSize: "14px" }}>or</span>
                            <hr style={{ flex: 1, border: "none", height: "1px", backgroundColor: "#ccc" }} />
                          </div>
                          <GoogleSignIn onSignInClick={this.handleGoogleClick} />
                        </>
                      </div>
                    </div>
                  </div>
                </div>

                <div className='text-center manrope f-12 w-400 grey-66 mt-40 mb-40'>
                  By continuing, you accept ALL3D's <a href="https://all3d.ai/terms">Terms of Use</a> and <a href="https://all3d.ai/privacy">Privacy Policy</a>.
                </div>
              </Col>
              <Col md={24} lg={12} className='show-on-mobile justify-in-center'>
                <img className="branding-image login-page-mobile-img" src={'/img/introImageMobile.png'} />
              </Col>
            </Row>
            <Footer/>
          </>
        ) : (
          <span>
            {this.state.stage === 2 ? (
              <Dashboard loginUser={this.loginUser} />
            ) : (
              <Dashboard
                registerUsername={this.state.email}
                registerPassword={this.state.password}
              />
            )}
          </span>
        )}
      </span>
    );
  };
}

MinimalRegisterForm.contextTypes = {
  store: PropTypes.object,
};

export default withRouter(MinimalRegisterForm);
